import { createContext, useContext, useState } from 'react';
import { useAppSelector } from 'src/redux';
import { useGetMyExperimentsQuery } from 'src/redux/services/experiments';
import { PRO_PLUS, type ProTierType } from './tiers';

/** The pitch's own words. The strip under the headline says why the candidate
 * is here, and the body (compare.tsx) says what Pro does, so there is no
 * subtitle. */
export interface PitchCopy {
  headline: string;
}

export interface PitchVariant extends Partial<PitchCopy> {
  /** The idea in a sentence, for the experiments admin's preview. */
  about: string;
  /** The tier the pitch shows first (tiers.ts); control's is Pro+. */
  leadTier?: ProTierType;
}

export const CONTROL_COPY: PitchCopy = {
  headline: "Here's what you're missing on Free",
};

/**
 * Pitch variants, by experiment key then variant name — the code half of an
 * experiment whose config lives in backend-api experiment/constants.py.
 * Control is never listed: it is CONTROL_COPY. An arm the server returns that
 * is missing here renders control, so a backend config can never show a blank
 * or broken pitch.
 *
 * Copy rules (see buyCreditsDialog/benefits.ts): AI-credit features are never
 * "free", one guidance call a month is, and nothing promises outcomes.
 */
export const PRO_PITCH_VARIANTS: Record<
  string,
  Record<string, PitchVariant>
> = {
  pro_tiers_v1: {
    lead_pro: {
      leadTier: 'premium_profile_pro',
      about:
        'Pro (299, no monthly 1:1 call) first; Pro+ and Pro Lite one tap away.',
    },
    lead_lite: {
      leadTier: 'premium_profile_lite',
      about:
        'Pro Lite (199, 1,000 AI credits, no call) first; Pro and Pro+ one tap away.',
    },
  },
};

/** What control is, for the admin's preview. */
export const CONTROL_ABOUT =
  'Free vs Pro+ in four rows, the wall they hit first; Pro and Pro Lite under "See all Pro options".';

/** The arm the backend registry lists first, rendered as CONTROL_COPY. */
const CONTROL_ARM = 'control';

/** How old a cached arm may be when a pitch opens before it is re-read. */
const ARM_MAX_AGE_S = 5 * 60;

/**
 * Renders the pitch as one arm whatever this user's own is -- the experiments
 * admin's preview of each design. Nothing is fetched, and the pitch reports no
 * funnel events while it is set (see CareerProPitch).
 */
export const ProPitchArmPreview = createContext<{
  experimentKey: string;
  arm: string;
} | null>(null);

/**
 * The pitch copy for this user's arm, the arm that copy is (`renderedArm`,
 * sent with every event so the server stamps a showing only when it matches
 * the stored arm), and whether that arm is settled yet -- a showing is only
 * reported once it is.
 */
export const useProPitchCopy = () => {
  const isLoggedIn = useAppSelector((state) => !!state.auth.isLoginSuccess);
  const userId = useAppSelector((state) => state.auth.user?.id);
  const preview = useContext(ProPitchArmPreview);
  // Re-read when the pitch opens on an arm older than this, so pausing an
  // experiment reaches open tabs at their next showing, not their next reload.
  const { data, isFetching, isError, fulfilledTimeStamp } =
    useGetMyExperimentsQuery(String(userId ?? ''), {
      skip: !!preview || !isLoggedIn || !userId,
      refetchOnMountOrArgChange: ARM_MAX_AGE_S,
    });
  // The re-read starts after the first render, which still holds the old arm:
  // that arm is not settled until it is re-read (or the re-read fails).
  const [openedAt] = useState(() => Date.now());
  const stale =
    !!fulfilledTimeStamp &&
    openedAt - fulfilledTimeStamp > ARM_MAX_AGE_S * 1000;
  const assignments = preview
    ? { [preview.experimentKey]: preview.arm }
    : (data?.assignments ?? {});
  const experimentKey = Object.keys(PRO_PITCH_VARIANTS).find(
    (key) => !!assignments[key],
  );
  const arm = experimentKey ? assignments[experimentKey] : undefined;
  const variantCopy =
    experimentKey && arm ? PRO_PITCH_VARIANTS[experimentKey][arm] : undefined;
  // An arm this build does not know renders control's copy, which is not
  // that arm: report none, and the showing counts in no arm.
  let renderedArm: string | undefined;
  if (variantCopy || arm === CONTROL_ARM) renderedArm = arm;
  return {
    copy: {
      ...CONTROL_COPY,
      ...(variantCopy?.headline && { headline: variantCopy.headline }),
    },
    leadTier: variantCopy?.leadTier ?? PRO_PLUS,
    renderedArm,
    // After any re-read too, so `shown` carries the arm finally on screen.
    settled:
      !!preview ||
      !isLoggedIn ||
      !userId ||
      (!isFetching && (!stale || isError)),
    preview: !!preview,
  };
};
